import { Check, FileText } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { ChoiceGroup } from "../wizard/ChoiceGroup";
import { PhotoUpload } from "../wizard/PhotoUpload";
import { ContactStep, Field, inputCls } from "../wizard/WizardSteps";
import { COLORS, PRODUCTS, postcodeError } from "../wizard/wizardOptions";
import { trackEvent } from "../../lib/api";
import { COMPARE_GLAZING, GOALS, INCLUDES, MAX_OFFER_FILES, VAT_OPTIONS, priceError } from "./compareOptions";
import { SizeRows } from "./SizeRows";

const CheckChips = ({ name, label, options, values, onChange }) => (
  <fieldset className="min-w-0">
    <legend className="mb-2.5 text-[14px] font-semibold text-ink">{label} <span className="font-normal text-slate-500">(optioneel, meerdere mogelijk)</span></legend>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = values.includes(o.id);
        return (
          <button key={o.id} type="button" role="checkbox" aria-checked={on} data-testid={`compare-${name}-${o.id}`}
            onClick={() => onChange(on ? values.filter((v) => v !== o.id) : [...values, o.id])}
            className={`inline-flex min-h-[42px] items-center gap-2 rounded-full border px-4 text-[14px] transition-[border-color,background-color] duration-150 ${on ? "border-maas bg-maas-soft font-semibold text-maas-deep" : "border-slate-200 bg-white text-ink hover:border-slate-300"}`}>
            {on && <Check className="h-4 w-4" aria-hidden="true" />}{o.label}
          </button>
        );
      })}
    </div>
  </fieldset>
);

export const OfferStep = ({ form, set, offers, setOffers, error }) => (
  <div className="space-y-7">
    <div className="space-y-5">
      <Field id="price" label="Totaalbedrag van uw offerte" note="(of upload uw offerte hieronder)" error={priceError(form.price)}>
        <div className="relative max-w-xs">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-slate-500">€</span>
          <Input id="price" inputMode="decimal" value={form.price} maxLength={14} placeholder="8.450" data-testid="compare-price"
            onChange={(e) => set("price", e.target.value)} className={`${inputCls(priceError(form.price))} pl-9`} />
        </div>
      </Field>
      <div className="max-w-lg"><ChoiceGroup name="btw" label="Is dit bedrag inclusief btw?" options={VAT_OPTIONS} value={form.vat} onChange={(v) => set("vat", v)} cols="grid-cols-3" /></div>
    </div>
    <PhotoUpload uploads={offers} setUploads={setOffers} label="Uw offerte als PDF, foto of screenshot" note="(of vul hierboven het bedrag in)" cta="Offerte uploaden"
      max={MAX_OFFER_FILES} testid="compare-offer" icon={FileText} onUploaded={() => trackEvent("compare_file_upload", { kind: "offerte" })} />
    {error && <p className="rounded-xl bg-amber-50 px-4 py-3 text-[14px] font-medium text-amber-800" role="alert" data-testid="compare-offer-error">{error}</p>}
    <Field id="competitor" label="Naam van het kozijnbedrijf">
      <Input id="competitor" value={form.competitor} maxLength={100} placeholder="Bijv. de naam op de offerte" data-testid="compare-competitor"
        onChange={(e) => set("competitor", e.target.value)} className={inputCls()} />
    </Field>
    <ChoiceGroup name="doel" label="Wat wilt u vooral weten?" options={GOALS} value={form.goal} onChange={(v) => set("goal", v)} cols="grid-cols-1 sm:grid-cols-2" />
  </div>
);

export const SpecsStep = ({ form, set }) => (
  <div className="space-y-7">
    <ChoiceGroup name="product" label="Waar gaat de offerte over?" options={PRODUCTS} value={form.product} onChange={(v) => set("product", v)} cols="grid-cols-1 sm:grid-cols-2" />
    <Field id="profile_system" label="Profielsysteem of merk uit de offerte">
      <Input id="profile_system" value={form.profile_system} maxLength={100} placeholder="Bijv. Gealan S 9000, Schüco of Kömmerling" data-testid="compare-profile-system"
        onChange={(e) => set("profile_system", e.target.value)} className={inputCls()} />
    </Field>
    <ChoiceGroup name="glas" label="Glas volgens de offerte" options={COMPARE_GLAZING} value={form.glazing} onChange={(v) => set("glazing", v)} />
    <ChoiceGroup name="kleur" label="Kleur" options={COLORS} value={form.color} onChange={(v) => set("color", v)} />
    <CheckChips name="inbegrepen" label="Wat staat er in de offerte?" options={INCLUDES} values={form.includes} onChange={(v) => set("includes", v)} />
  </div>
);

export const SizesStep = ({ form, set, items, setItems, photos, setPhotos }) => (
  <div className="space-y-7">
    <SizeRows items={items} setItems={setItems} />
    <PhotoUpload uploads={photos} setUploads={setPhotos} label="Foto's van uw huidige situatie" testid="compare-photo"
      onUploaded={() => trackEvent("compare_file_upload", { kind: "foto" })} />
    <Field id="opmerkingen" label="Opmerkingen of vragen">
      <Textarea id="opmerkingen" rows={3} maxLength={2000} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="compare-notes"
        placeholder="Bijv. de offerte is twee weken geldig en ik twijfel over het glas." className="rounded-xl border-slate-300 text-[15px]" />
    </Field>
    <p className="text-[13px] text-slate-500">Staan de maten al in uw offerte? Dan kunt u deze stap gewoon overslaan.</p>
  </div>
);

export const CompareContactStep = ({ form, set, errors }) => (
  <div className="space-y-4">
    <ContactStep form={form} set={set} errors={errors} />
    <div className="grid gap-4 sm:grid-cols-2">
      <Field id="postcode" label="Postcode" error={postcodeError(form.postcode)}>
        <Input id="postcode" value={form.postcode} maxLength={7} autoComplete="postal-code" placeholder="3142 AB" data-testid="compare-postcode"
          onChange={(e) => set("postcode", e.target.value.toUpperCase())} className={inputCls(postcodeError(form.postcode))} />
      </Field>
      <Field id="woonplaats" label="Woonplaats">
        <Input id="woonplaats" value={form.woonplaats} maxLength={80} autoComplete="address-level2" placeholder="Maassluis" data-testid="compare-woonplaats"
          onChange={(e) => set("woonplaats", e.target.value)} className={inputCls()} />
      </Field>
    </div>
  </div>
);
